<!-- Custom Dialog Layout with Fixed Header -->
<div class="order-dialog-container">
  <!-- Fixed Header with Title -->
  <div class="fixed-header">
    <!-- Dialog Title Section -->
    <div class="dialog-title-section">
      <div class="flex justify-between items-start">
        <div>
          <h1 class="text-xl font-medium text-on-surface">Order Details</h1>
          <p class="text-sm mt-2 text-on-surface-variant">View and manage order information</p>
        </div>
        <button
          type="button"
          class="close-button"
          (click)="closeDialog()"
          aria-label="Close dialog"
        >
          <mat-icon svgIcon="close" class="w-6 h-6"></mat-icon>
        </button>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div>
      <div class="flex space-x-1 border-b border-outline-variant">
        <button
          class="px-4 py-2 text-sm font-medium transition-colors duration-200"
          [class]="selectedTabIndex === 0 ? 'text-primary border-b-2 border-primary' : 'text-on-surface-variant hover:text-on-surface'"
          (click)="selectTab(0)"
        >
          Order Information
        </button>
        <button
          class="px-4 py-2 text-sm font-medium transition-colors duration-200"
          [class]="selectedTabIndex === 1 ? 'text-primary border-b-2 border-primary' : 'text-on-surface-variant hover:text-on-surface'"
          (click)="selectTab(1)"
        >
          Customer Information
        </button>
        <button
          class="px-4 py-2 text-sm font-medium transition-colors duration-200"
          [class]="selectedTabIndex === 2 ? 'text-primary border-b-2 border-primary' : 'text-on-surface-variant hover:text-on-surface'"
          (click)="selectTab(2)"
        >
          Order Items
        </button>
        <button
          class="px-4 py-2 text-sm font-medium transition-colors duration-200"
          [class]="selectedTabIndex === 3 ? 'text-primary border-b-2 border-primary' : 'text-on-surface-variant hover:text-on-surface'"
          (click)="selectTab(3)"
        >
          Order Logs
        </button>
      </div>
    </div>
  </div>

  <!-- Scrollable Content Area -->
  <div class="flex-1 overflow-y-auto">
    <div class="px-11 py-6">
      <!-- Tab Content -->
      @if (orderDetail$ | async; as order) {
        <!-- Order Information Tab Content -->
        @if (selectedTabIndex === 0) {
          <div class="space-y-6">
            <!-- Order Header Section -->
            <div class="order-summary-section p-4">
              <h3 class="text-lg font-semibold mb-4 text-on-surface">Order Header</h3>
              @if (order.hasPreOrderItems) {
                <p class="text-sm font-semibold mb-3 text-primary">Pre-delivery</p>
              }
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label class="block text-sm font-medium text-on-surface-variant">Order ID</label>
                  <p class="mt-1 text-sm text-on-surface font-semibold">#{{ order.id }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-on-surface-variant">Full Name</label>
                  <p class="mt-1 text-sm text-on-surface">{{ order.customerFullName }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-on-surface-variant">Order Date</label>
                  <p class="mt-1 text-sm text-on-surface">{{ order.orderDate | date:'yyyy/MM/dd HH:mm' }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-on-surface-variant">Total Amount</label>
                  <p class="mt-1 text-sm text-on-surface font-semibold">{{ order.prices?.totalOrderPrice | kwdCurrency }}</p>
                </div>
              </div>

              <!-- Status and Actions -->
              <div class="order-status-row mt-4">
                <div class="flex-1 min-w-48">
                  <cac-field
                    [control]="statusForm.controls.status"
                    label="Order Status"
                    controlType="select"
                    [items]="getFilteredStatusOptions(order.status)"
                    [searchable]="false"
                    [clearable]="false"
                  ></cac-field>
                </div>
                <cac-button
                  theme="primary"
                  (onClick)="onUpdateStatus($event)"
                  [disabled]="statusForm.controls.status.value === order.status || statusForm.controls.status.disabled"
                >
                  Update Status
                </cac-button>
              </div>

              <!-- Order Note Display -->
              @if (order.orderNote) {
                <div class="mt-4">
                  <div>
                    <label class="block text-sm font-medium text-on-surface-variant">Order Note</label>
                    <p class="mt-1 text-sm text-on-surface bg-surface-variant/20 p-3 rounded-md border border-outline-variant">{{ order.orderNote }}</p>
                  </div>
                </div>
              }
            </div>

            <!-- Payment Information & Pricing -->
            <div class="order-summary-section p-4">
              <h3 class="text-lg font-semibold mb-4 text-on-surface">Payment Information & Pricing</h3>

              <!-- Payment Method and Actions -->
              <div class="order-status-row mb-6">
                <div class="flex-1 min-w-48">
                  <cac-field
                    [control]="paymentMethodForm.controls.paymentMethodId"
                    label="Payment Method"
                    controlType="select"
                    [items]="paymentMethodOptions"
                    [searchable]="false"
                    [clearable]="false"
                  ></cac-field>
                </div>
                <cac-button
                  theme="primary"
                  (onClick)="onUpdatePaymentMethod($event)"
                  [disabled]="paymentMethodForm.controls.paymentMethodId.value === order.paymentMethodId || paymentMethodForm.controls.paymentMethodId.disabled"
                >
                  Update
                </cac-button>
              </div>

              <!-- Shipping Cost and Actions -->
              <div class="order-status-row mb-6">
                <div class="flex-1 min-w-48">
                  <cac-field
                    [control]="shippingCostForm.controls.shippingCost"
                    label="Delivery Fee"
                    type="number"
                  ></cac-field>
                </div>
                <cac-button
                  theme="primary"
                  (onClick)="onUpdateShippingCost($event)"
                  [disabled]="shippingCostForm.controls.shippingCost.value === order.prices?.shippingFee || shippingCostForm.controls.shippingCost.disabled"
                >
                  Update
                </cac-button>
              </div>

              <!-- Coupon Code -->
              @if (order.couponCode) {
                <div class="mb-6">
                  <div>
                    <label class="block text-sm font-medium text-on-surface-variant">Coupon Code</label>
                    <p class="mt-1 text-sm text-on-surface font-medium">{{ order.couponCode }}</p>
                  </div>
                </div>
              }

              <!-- Pricing Breakdown -->
              <div class="border-t border-outline-variant pt-4">
                <h4 class="text-base font-medium mb-3 text-on-surface">Pricing Breakdown</h4>
                @if (order.prices) {
                  <div class="space-y-3">
                    <div class="flex justify-between">
                      <span class="text-sm text-muted">Items Price:</span>
                      <span class="text-sm font-medium text-on-surface">{{ order.prices.itemsPrice | kwdCurrency }}</span>
                    </div>

                    @if (order.prices.itemsDiscount && order.prices.itemsDiscount > 0) {
                      <div class="flex justify-between text-success">
                        <span class="text-sm">Items Discount:</span>
                        <span class="text-sm font-medium">-{{ order.prices.itemsDiscount | kwdCurrency }}</span>
                      </div>
                    }

                    <div class="flex justify-between">
                      <span class="text-sm text-muted">Shipping Fee:</span>
                      <span class="text-sm font-medium text-on-surface">{{ order.prices.shippingFee | kwdCurrency }}</span>
                    </div>

                    <div class="flex justify-between" [class.text-success]="order.prices.shippingFeeDiscount > 0">
                      <span class="text-sm" [class.text-muted]="order.prices.shippingFeeDiscount === 0">Shipping Fee Discount:</span>
                      <span class="text-sm font-medium">{{ order.prices.shippingFeeDiscount > 0 ? '-' : '' }}{{ order.prices.shippingFeeDiscount | kwdCurrency }}</span>
                    </div>

                    @if (order.prices.totalCouponCodeDiscount && order.prices.totalCouponCodeDiscount > 0) {
                      <div class="flex justify-between text-success">
                        <span class="text-sm">Coupon Code Discount:</span>
                        <span class="text-sm font-medium">-{{ order.prices.totalCouponCodeDiscount | kwdCurrency }}</span>
                      </div>
                    }

                    <hr class="border-outline-variant">

                    <div class="flex justify-between text-lg font-semibold text-on-surface">
                      <span>Total Order Price:</span>
                      <span>{{ order.prices.totalOrderPrice | kwdCurrency }}</span>
                    </div>
                  </div>
                } @else {
                  <p class="text-sm text-muted">Pricing information not available</p>
                }
              </div>
            </div>
          </div>
        }

        <!-- Customer Information Tab Content -->
        @if (selectedTabIndex === 1) {
          <div class="space-y-6">
            <!-- Customer Details Form -->
            <div class="order-summary-section p-4">
              <h3 class="text-lg font-semibold mb-4 text-on-surface">Customer Details</h3>
              <div class="grid grid-cols-2 gap-3">
                <cac-field [control]="customerForm.controls.customerFullName" label="Full Name" />
                <cac-field [control]="customerForm.controls.customerEmail" label="Email" type="email" />
                <div class="col-span-2 grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3 items-start" dir="ltr">
                  <lib-country-code-field [control]="countryCode" />
                  <cac-field [control]="customerForm.controls.customerPhoneNumber" label="Phone Number" type="tel" />
                </div>
              </div>
            </div>

            <!-- Shipping Address Form -->
            <div class="order-summary-section p-4">
              <h3 class="text-lg font-semibold mb-4 text-on-surface">Shipping Address</h3>
              <cac-form-builder [options]="addressForm"></cac-form-builder>
            </div>


          </div>
        }

        <!-- Order Items Tab Content -->
        @if (selectedTabIndex === 2) {
          <div class="space-y-4">
            <cac-table [options]="orderItemsTable"></cac-table>
          </div>
        }

        <!-- Order Logs Tab Content -->
        @if (selectedTabIndex === 3) {
          <div class="space-y-4">
            <cac-table [options]="orderLogsTable"></cac-table>
          </div>
        }
      }
    </div>
  </div>

  <!-- Fixed Footer with Action Buttons -->
  @if (orderDetail$ | async; as order) {
    <div class="fixed-footer">
      <div class="action-controls flex justify-between items-center w-full">
        <!-- Left side - General action buttons -->
        <div class="flex items-center gap-2">
          <cac-button
            theme="error"
            (onClick)="onDeleteOrder($event)"
            [disabled]="order.status === 5 || order.status === 6"
          >
            Delete Order
          </cac-button>

          <cac-button
            theme="secondary"
            (onClick)="onDownloadPdf($event)"
          >
            Download PDF
          </cac-button>

          <cac-button
            theme="primary"
            (onClick)="onSendOrderToErp($event)"
            [disabled]="order.status === 0"
          >
            Send to ERP
          </cac-button>
        </div>

        <!-- Right side - Tab-specific action buttons -->
        <div class="flex items-center gap-2">
          @if (selectedTabIndex === 1) {
            <cac-button theme="primary" (onClick)="onUpdateCustomerInfo($event)">
              Update Customer Information
            </cac-button>
          }
        </div>
      </div>
    </div>
  }
</div>
